// ============================================
// STORAGE - API-backed state management
// ============================================

import { AppState, WeekSchedule } from "./types.js";
import { getWeekStart, getNextWeekStart } from "./shifts-logic.js";
import { fetchWeek } from "./api.js";

/**
 * Get the default week start (current week's Sunday).
 */
function getDefaultWeekStart(): string {
  const today = new Date();
  return getWeekStart(today);
}

/**
 * Create default app state.
 */
export function createDefaultState(): AppState {
  const weekStart = getDefaultWeekStart();
  return {
    currentWeekStart: weekStart,
    weeks: {},
  };
}

/**
 * Load state.
 */
export function loadState(): AppState {
  return createDefaultState();
}

/**
 * Get week schedule - fetch from API if not cached.
 */
export async function getOrCreateWeek(
  state: AppState,
  weekStart: string,
): Promise<WeekSchedule> {
  if (!state.weeks[weekStart]) {
    try {
      state.weeks[weekStart] = await fetchWeek(weekStart);
    } catch (e) {
      console.error("Failed to fetch week from API:", e);
      throw e;
    }
  }
  return state.weeks[weekStart];
}

/**
 * Update week in cache (after API call).
 */
export function updateWeekCache(state: AppState, week: WeekSchedule): void {
  state.weeks[week.weekStart] = week;
}

/**
 * Set current week.
 */
export function setCurrentWeek(state: AppState, weekStart: string): void {
  state.currentWeekStart = weekStart;
}

// ============================================
// FORCE MODE - Allows setting shifts that are blocked/unavailable
// ============================================

const FORCE_MODE_KEY = "mishmarot_force_mode";

/**
 * Get persisted force mode setting.
 */
export function getForceMode(): boolean {
  return localStorage.getItem(FORCE_MODE_KEY) === "1";
}

/**
 * Persist force mode setting.
 */
export function setForceMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(FORCE_MODE_KEY, "1");
  } else {
    localStorage.removeItem(FORCE_MODE_KEY);
  }
}
